import { Component } from '@theme/component';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * @typedef {object} HowToFitRefs
 * @property {HTMLVideoElement} video
 * @property {HTMLElement} controls
 * @property {HTMLButtonElement} playOverlayButton
 * @property {HTMLButtonElement} playButton
 * @property {HTMLButtonElement} muteButton
 * @property {HTMLElement} playIcon
 * @property {HTMLElement} pauseIcon
 * @property {HTMLElement} mutedIcon
 * @property {HTMLElement} unmutedIcon
 */

/**
 * Controls playback for the How to Fit instructional video.
 *
 * @extends {Component<HowToFitRefs>}
 */
class HowToFitVideo extends Component {
  requiredRefs = [
    'video',
    'controls',
    'playOverlayButton',
    'playButton',
    'muteButton',
    'playIcon',
    'pauseIcon',
    'mutedIcon',
    'unmutedIcon',
  ];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLVideoElement | null} */
  #preparedVideo = null;

  #reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

  #userPaused = false;

  #pausedByViewport = false;

  #isVisible = false;

  #intersectionObserver = new IntersectionObserver(
    ([entry]) => {
      this.#isVisible = Boolean(entry?.isIntersecting);

      if (!this.#isVisible) {
        if (!this.refs.video.paused) {
          this.#pausedByViewport = true;
          this.refs.video.pause();
        }
        return;
      }

      const shouldAutoplay = this.dataset.autoplay === 'true';
      const shouldResume = this.#pausedByViewport || shouldAutoplay;
      if (shouldResume && !this.#userPaused && !this.#reducedMotion.matches && this.refs.video.paused) {
        this.#pausedByViewport = false;
        this.#play();
      }
    },
    { threshold: 0.2 }
  );

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#preparedVideo = null;
    this.#intersectionObserver.disconnect();
    this.refs.video.pause();
  }

  async togglePlayback() {
    const { video } = this.refs;

    if (video.paused || video.ended) {
      this.#userPaused = false;
      this.#pausedByViewport = false;
      await this.#play();
    } else {
      this.#userPaused = true;
      video.pause();
    }
  }

  toggleMute() {
    this.refs.video.muted = !this.refs.video.muted;
    this.#updateControls();
  }

  #setup() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { video } = this.refs;

    // The fallback image Shopify's video_tag renders inside <video> has no alt attribute; it is decorative.
    for (const img of video.querySelectorAll('img:not([alt])')) {
      img.setAttribute('alt', '');
    }

    for (const eventName of ['play', 'pause', 'ended', 'volumechange', 'loadeddata']) {
      video.addEventListener(eventName, this.#updateControls, { signal });
    }

    this.#reducedMotion.addEventListener('change', this.#handleMotionPreference, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

    this.#intersectionObserver.disconnect();
    this.#intersectionObserver.observe(this);

    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    if (this.#preparedVideo !== video) video.preload = 'none';
    video.muted = true;
    this.#updateControls();
  }

  #play = async () => {
    const { video } = this.refs;
    this.#prepareVideo(video);

    try {
      await video.play();
      return;
    } catch (error) {
      const shouldRetryMuted = error instanceof Error && error.name === 'NotAllowedError' && !video.muted;
      if (!shouldRetryMuted) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.warn('Unable to play How to Fit video.', error);
        }
        this.#updateControls();
        return;
      }
    }

    video.muted = true;

    try {
      await video.play();
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('Unable to play How to Fit video.', error);
      }
      this.#updateControls();
    }
  };

  /**
   * Initializes one video on demand without loading every offscreen PDP video.
   *
   * @param {HTMLVideoElement} video
   */
  #prepareVideo(video) {
    if (this.#preparedVideo === video) return;

    this.#preparedVideo = video;
    video.preload = 'metadata';
    if (video.readyState === 0) video.load();
  }

  #handleMotionPreference = () => {
    if (this.#reducedMotion.matches) {
      this.refs.video.pause();
      return;
    }

    if (!this.#userPaused && this.#isVisible && this.dataset.autoplay === 'true') {
      this.#play();
    }
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      if (!this.refs.video.paused) {
        this.#pausedByViewport = true;
        this.refs.video.pause();
      }
      return;
    }

    const shouldAutoplay = this.dataset.autoplay === 'true';
    const shouldResume = this.#pausedByViewport || shouldAutoplay;
    if (shouldResume && this.#isVisible && !this.#userPaused && !this.#reducedMotion.matches && this.refs.video.paused) {
      this.#pausedByViewport = false;
      this.#play();
    }
  };

  #updateControls = () => {
    const { video, playOverlayButton, playButton, muteButton, playIcon, pauseIcon, mutedIcon, unmutedIcon } =
      this.refs;
    const isPlaying = !video.paused && !video.ended;
    const isMuted = video.muted;

    this.dataset.playing = String(isPlaying);

    playIcon.hidden = isPlaying;
    pauseIcon.hidden = !isPlaying;
    mutedIcon.hidden = !isMuted;
    unmutedIcon.hidden = isMuted;

    playOverlayButton.setAttribute('aria-label', this.#label('play'));
    playButton.setAttribute('aria-pressed', String(isPlaying));
    playButton.setAttribute('aria-label', isPlaying ? this.#label('pause') : this.#label('play'));
    muteButton.setAttribute('aria-pressed', String(isMuted));
    muteButton.setAttribute('aria-label', isMuted ? this.#label('unmute') : this.#label('mute'));
  };

  /**
   * @param {'play' | 'pause' | 'mute' | 'unmute'} action
   * @returns {string}
   */
  #label(action) {
    const key = `label${action.charAt(0).toUpperCase()}${action.slice(1)}`;
    return this.dataset[key] || `${action} video`;
  }
}

if (!customElements.get('how-to-fit-video')) {
  customElements.define('how-to-fit-video', HowToFitVideo);
}
